import React from 'react';
import PropTypes from 'prop-types';

import Actions from '@app/action';
import PreviewHelper from '@app/helper/preview';
import Store from '@app/store';
import Browser from '@wetransfer/concorde-browser';
import I18n from '@module/i18n';
import Router from '@module/router';
import { itemHeight } from '@module/transfer/preview';

import Button from '@component/form/element/button';
import FileWrapper from '@component/preview/file-wrapper';
import GridAction from '@component/preview/grid-action';
import Icon from '@component/icon';
import Loader from '@component/loader';
import Spinner from '@component/spinner';

export default class Grid extends React.Component {
  static propTypes = {
    items: PropTypes.array,
    route: PropTypes.object,
    setReturnPath: PropTypes.func,
    page: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.renderItem = this.renderItem.bind(this);
    this.renderItems = this.renderItems.bind(this);
  }

  UNSAFE_componentWillUpdate() {
    if (!Browser.isMobile) {
      const downloadUrl = this.props.route.path.split('/grid')[0];
      Router.setLastRoute('transfer', downloadUrl);
    }
  }

  renderItems(items) {
    const shuffledItems = this.shuffleItems(items);
    return (
      <div className="grid__layout">
        <div className="row">
          <div className="col">
            {shuffledItems.leftItems.map(this.renderItem)}
          </div>
          <div className="col">
            {shuffledItems.rightItems.map(this.renderItem)}
          </div>
        </div>
      </div>
    );
  }

  shuffleItems(items) {
    // Total height of left column, sum of all images' height
    let leftTotalHeight = 0;
    // Total height of right column, sum of all images' height
    let rightTotalHeight = 0;

    // Items rendered on the left column
    const leftItems = [];
    // Items rendered on the right column
    const rightItems = [];

    items.forEach((item) => {
      const { type, metadata } = item;
      const nodeDimensions = { height: 80, width: 400 };
      const height = itemHeight[type]
        ? itemHeight[type](nodeDimensions, metadata)
        : 80;
      if (rightTotalHeight - leftTotalHeight < 0) {
        rightItems.push(item);
        rightTotalHeight += height;
      } else {
        leftItems.push(item);
        leftTotalHeight += height;
      }
    });

    return {
      leftItems,
      rightItems,
    };
  }

  get scrollElement() {
    return (
      this.props.page &&
      this.props.page.scrollableNode &&
      this.props.page.scrollableNode.contentNode
    );
  }

  onItemClick = (itemId) => {
    if (Browser.isMobile) {
      return;
    }

    const {
      transfer: { id, recipient_id, secret },
    } = Store.getState();

    PreviewHelper.goToPreview(id, secret, recipient_id, itemId);
  };

  renderItem(item) {
    if (item.content_identifier !== 'file') {
      return;
    }

    let icon = <Icon type="DownloadSmall" />;
    if (
      item.file &&
      item.file.state &&
      item.file.state.download === 'downloading'
    ) {
      icon = (
        <Spinner
          rotate={true}
          percentage={25}
          displayText={false}
          circumference={24}
          stroke={2}
        />
      );
    }

    if (item.file && item.file.state && item.file.state.download === 'done') {
      icon = <Icon type="CheckCircle" />;
    }

    const action = {
      alternativeAction: (
        <GridAction onClick={this.onDownload} itemId={item.id}>
          {icon}
        </GridAction>
      ),
    };

    return (
      <div key={item.id}>
        <FileWrapper
          file={item.file}
          itemId={item.id}
          action={action}
          loadingIndicator={
            <Loader
              animation={false}
              backgroundColor="#E8EBED"
              pulseEnabled={true}
            />
          }
          onClick={this.onItemClick}
          scrollEl={this.scrollElement}
          errorAction={false}
          className={'grid__card'}
        />
      </div>
    );
  }

  onDownloadAll = async () => {
    const { transfer } = Store.getState();

    if (transfer.currentFileDownload) {
      return;
    }

    const { params } = this.props.route;

    await Store.dispatch(
      Actions.Transfer.download(params.secret, params.recipient_id || false)
    );
  };

  onDownload = (itemId) => {
    let params = {};
    const { transfer } = Store.getState();

    if (this.props.route) {
      params = this.props.route.params;
    } else {
      params = Store.getState().route.params;
    }

    Store.dispatch(
      Actions.Transfer.downloadFile(
        transfer.version || params.secret,
        params.recipient_id,
        itemId
      )
    );
  };

  render() {
    const { transfer } = Store.getState();
    const items = this.props.items || transfer.items;
    if (!items) {
      return null;
    }

    const title = transfer.title || I18n.t('transfer.preview.grid');
    let expires = I18n.t('transfer.download.expires', {
      relative_time: I18n.relativeTime(transfer.expiry_seconds),
    });

    if (typeof transfer.expiry_seconds === 'undefined') {
      expires = I18n.t('transfer.download.expires_never');
    }

    if (transfer.isSpaceship) {
      if (transfer.state === 'processing') {
        expires = I18n.t('transfer.collection.uploading.copy');
      } else if (!transfer.items.length) {
        expires = I18n.t('transfer.collection.empty.copy');
      }
    }

    const description = I18n.t('transfer.download.format', {
      expires,
      files: I18n.t('transfer.download.files', {
        count: items.length,
        size: I18n.size(transfer.totalSize || 0, { precision: 0 }),
      }),
    });

    return (
      <div className="grid-container">
        <div className="grid__bar">
          <div className="grid__meta">
            <div className="grid__title">{title}</div>
            <div className="grid__subtitle">{description}</div>
          </div>
          <Button
            onClick={this.onDownloadAll}
            text={I18n.t('transfer.buttons.download_all')}
            className="u-margin-left"
          />
        </div>
        {this.renderItems(items)}
      </div>
    );
  }
}
